import { Box, Button, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { ReactNode } from 'react';

import './NavigationBox.scss';

interface NavigationBoxProps {
	/** Icon rendered at the top of the navigation box. */
	icon: ReactNode;
	/** Primary label for the navigation item. */
	title: string;
	/** Short description displayed below the title. */
	description: string;
	/** Callback invoked when the box is clicked. */
	onPress: () => void;
	/** Optional badge rendered alongside the title. */
	badge?: ReactNode;
}

/**
 * A clickable box used for section-level navigation (e.g. Settings, Reports).
 * Uses a Rev Button with null variant for interactivity and focus handling.
 */
export function NavigationBox({
	icon,
	title,
	description,
	onPress,
	badge,
}: NavigationBoxProps): ReactNode {
	return (
		<Button
			variant={null}
			className='navigation-box'
			onPress={onPress}
			elevation='1'
			ariaLabel={title}
			contentAlignX='start'
			contentAlignY='start'>
			<Box padding={12} background='primary' rounding='12' showOverflow>
				{badge && <div className='navigation-box-badge'>{badge}</div>}
				<Group noWrap itemsSpacing='12' itemsAlignY='start'>
					<Box
						className='navigation-box-icon'
						padding={{ all: 4 }}
						rounding='8'
						background='secondary'>
						{icon}
					</Box>
					<Stack itemsSpacing='4'>
						<Text size='bodySmall' weight='semibold'>
							{title}
						</Text>
						<Text size='footnote' color='secondary'>
							{description}
						</Text>
					</Stack>
				</Group>
			</Box>
		</Button>
	);
}
